// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2020 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import { ButtonPresentationProps, ButtonProps } from "@adl/MobiusOverrides/Button/Button";
import { DataTableHeaderProps } from "@adl/MobiusOverrides/DataTable/DataTableHeader";
import { color, xScreen } from "@adl/XDL/Utilities";
import { pageTitleStyle } from "@adl/XDL/Utilities/sharedStyles";
import { InvoiceHistoryPaginationStyles } from "@insite/content-library/Widgets/InvoiceHistory/InvoiceHistoryPagination";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { LinkProps } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface InvoicePaymentStyles {
    pageTitle?: TypographyProps;
    header?: {
        wrapper?: InjectableCss;
        wrapperWithBg?: InjectableCss;
        title?: TypographyProps;
    };
    content?: {
        wrapper?: InjectableCss;
        wrapperWithBg?: InjectableCss;
        title?: TypographyProps;
    };
    notifyMessage?: any;
    loading?: any;
    balanceDueCell?: DataTableHeaderProps;
    balanceDueWrapper?: InjectableCss;
    emptyCell?: DataTableCellProps;
    shipToCell?: {
        wrapper?: DataTableHeaderProps;
        text?: TypographyProps;
    };
    paymentAmountCell?: DataTableHeaderProps;
    tableWrapper?: InjectableCss;
    textFieldStyle?: TextFieldProps;
    makePaymentBtn?: ButtonPresentationProps;
    ccPaymentNotifyWrapper?: InjectableCss;
    ccAndPaymentWrapper?: InjectableCss;
    invoiceId?: LinkProps;
    noteResultWrapper?: InjectableCss;
    noteField?: InjectableCss;
    resultSection?: InjectableCss;
    alternatePaymentAmountField?: TextFieldProps;
    payAllInvoicesWrapper?: InjectableCss;
    payAllInvoicesBtn?: ButtonProps;
    overDueInvoice?: DataTableRowProps;
    overDueInvoiceDate?: DataTableCellProps;
    paginationWrapper?: InjectableCss;
    pagination?: InvoiceHistoryPaginationStyles;
}

const specificCellWidth = 210;
export const invoicePaymentStyle: InvoicePaymentStyles = {
    pageTitle: {
        ...pageTitleStyle,
        css: css`
            margin: 20px 0 30px 0;
            font-weight: 700;
        `,
    },
    notifyMessage: {
        wrapper: {
            css: css`
                display: flex;
                justify-content: flex-start;
                margin: 30px 0;
            `,
        },
    },
    loading: {
        wrapper: {
            css: css`
                display: flex;
                justify-content: center;
                align-item: center;
                margin: 30px 0;
                min-height: 300px;
                align-items: center;
            `,
        },
    },
    balanceDueCell: {
        css: css`
            width: ${specificCellWidth}px;
        `,
    },
    balanceDueWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            > *:first-child {
                margin-bottom: 8px;
            }
        `,
    },
    emptyCell: {
        css: css`
            && {
                background: white;
                border: 0;
                padding: 0;
                min-width: 40px;
                max-width: 40px;
                width: 40px;
            }
            ${xScreen("lte", "sm")} {
                &&& {
                    display: none;
                }
            }
        `,
    },
    shipToCell: {
        wrapper: {
            css: css`
                width: 220px;
                max-width: 220px;
                min-width: 220px;
                white-space: break-spaces;
                ${xScreen("lte", "sm")} {
                    max-width: 100%;
                }
            `,
        },
        text: {
            css: css`
                white-space: break-spaces;
            `,
        },
    },
    paymentAmountCell: {
        css: css`
            width: ${specificCellWidth}px;
            max-width: ${specificCellWidth}px;
            min-width: ${specificCellWidth}px;
            white-space: normal;
            text-align: center;
            &.value-cell {
                background: ${color("common.ui3")};
                text-align: left;
                padding: 16px;
            }
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                &:before {
                    text-align: left;
                }
                &&&&,
                &&&& > div,
                &&&& > button {
                    max-width: 100%;
                    margin-top: 16px;
                    input {
                        height: 30px;
                    }
                }
            }
        `,
    },
    tableWrapper: {
        css: css`
            margin-bottom: 36px;
            > div {
                box-shadow: none;
            }
            tr:last-child {
                td:not(:nth-child(7)) {
                    border-bottom: 2px solid ${color("common.border")};
                    ${xScreen("lte", "xs")} {
                        border-bottom: 0;
                    }
                }
            }
        `,
    },
    textFieldStyle: {
        cssOverrides: {
            descriptionWrapper: css`
                white-space: break-spaces;
            `,
            formInputWrapper: css`
                input {
                    text-align: center;
                    height: 30px;
                }
            `,
        },
    },
    makePaymentBtn: {
        css: css`
            width: 100%;
            margin-top: 24px;
        `,
    },
    ccPaymentNotifyWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            span[data-test-selector="notify-danger"] {
                width: 100%;
                margin-top: 40px;
                ${xScreen("lte", "sm")} {
                    margin-top: 16px;
                }
            }
        `,
    },
    ccAndPaymentWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            padding-top: 36px;
            justify-content: space-between;
            ${xScreen("lte", "680")} {
                flex-direction: column;
            }
            ${xScreen("lte", "700")} {
                .total_paymentAmount {
                    margin-top: 30px;
                }
            }
        `,
    },
    invoiceId: {
        css: css`
            line-height: 26px;
            font-weight: bold;
            text-decoration: underline;
        `,
    },
    noteResultWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding-bottom: 36px;
            border-bottom: 1px solid ${color("common.border")};
            ${xScreen("lte", "sm")} {
                flex-wrap: wrap;
            }
        `,
    },
    noteField: {
        css: css`
            &&& {
                width: 308px;
                max-width: 100%;
                ${xScreen("lte", "sm")} {
                    margin-bottom: 16px;
                    width: 100%;
                }
            }
        `,
    },
    resultSection: {
        css: css`
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    alternatePaymentAmountField: {
        cssOverrides: {
            formField: css`
                width: 220px;
            `,
            inputSelect: css`
                height: 42px;
                text-align: center;
                ${xScreen("lte", "xs")} {
                    margin-top: 10px;
                }
            `,
        },
    },
    payAllInvoicesWrapper: {
        css: css`
            display: flex;
            align-items: end;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                > div {
                    width: 100%;
                }
                > button {
                    width: 100%;
                    margin: 16px 0 16px;
                }
            }
        `,
    },
    payAllInvoicesBtn: {
        css: css`
            margin-right: 40px;
            margin-top: 16px;
        `,
    },
    overDueInvoice: {
        css: css`
            &&& {
                td:not(:last-child):not(:nth-child(7)) {
                    background: ${color("danger.light")};
                }
            }
        `,
    },
    overDueInvoiceDate: {
        css: css`
            color: ${color("danger.main")};
            font-weight: bold;
        `,
    },
    paginationWrapper: {
        css: css`
            margin-top: 16px;
        `,
    },
};

export const invoicePaymentTotalElementStyle = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    header: {
        wrapper: {
            css: css`
                width: auto;
                display: flex;
                align-items: center;
            `,
        },
        wrapperWithBg: {
            css: css`
                width: auto;
                display: flex;
                align-items: center;
                background: ${color("common.ui6")};
                padding: 8px 16px 4px;
                min-width: 220px;
                box-shadow: 0 1px 0 0 ${color("common.ui6")};
            `,
        },
        title: {
            css: css`
                text-transform: capitalize;
                font-weight: bold;
                line-height: 26px;
            `,
        },
    },
    content: {
        wrapper: {
            css: css`
                box-sizing: border-box;
                margin-top: 20px;
                text-align: left;
            `,
        },
        wrapperWithBg: {
            css: css`
                box-sizing: border-box;
                margin-top: 16px;
                text-align: right;
            `,
        },
        text: {
            size: 15,
            css: css`
                color: ${(props: any) => props.isError && color("danger")};
                font-weight: bold;
                white-space: break-spaces;
                line-height: 20px;
            `,
        },
    },
};
